// src/utils/changelogData.js

/**
 * Changelog data for the Pokémon TCG Deck Builder.
 * Each object in the array represents a version.
 * - version: The version number (string).
 * - date: Release date (string, optional).
 * - title: Descriptive title for the version (string, optional).
 * - isOpen: If true, this version's details are typically shown expanded by default in the UI (boolean).
 * - changes: Array of change categories. Each category has:
 * - title: Title of the category (string).
 * - items: Array of strings describing individual changes.
 * - Keywords (e.g., ADDED:, FIXED:) should be plain text followed by a colon.
 * - Subsection titles within items should be bolded with a colon: **Subsection Title:**
 */
const changelogData = [
  {
    version: "2.0.0",
    date: "2025-05-20", // Ensure this date is current for release
    title: "2.0 First Official Meta-PTCG Fork",
    isOpen: true, // This version's details will be open by default
    changes: [
      {
        title: "Key Features & Enhancements",
        items: [
          "**Revamped Card Search Panel (`EnhancedCardSearchPanel.js`):**",
          "ADDED: Default Load: Displays cards from the latest Pokémon TCG set on initial load for faster startup.",
          "CHANGED: Manual Search Trigger: Card searches are now initiated only by pressing the \"Search\" button.",
          "IMPROVED: Filter Panel Behavior: Search filter accordion now automatically minimizes when a search is executed.",
          "IMPROVED: Card Preview: Clicking a card image in search results now opens an enhanced preview modal (`CardPreviewModal.js`).",
          "ADDED: Custom Set Tabs: Integrated `CustomSetTabs.js` for managing and navigating user-created custom card sets.",
          "**Advanced Deck Export (`EnhancedExportModal.js`):**",
          "ADDED: Introduced a comprehensive export modal with options for multiple formats (Text, CSV, XML, JSON) and advanced data inclusion settings.",
          "**Deck Image Generation (`ImprovedDeckImageModal.js`):**",
          "ADDED: New modal for creating customizable deck images with various layouts (grid, stacked, fanned, showcase), backgrounds, card counters, and optional chart overlays.",
          "ADDED: Features for sorting cards within the image, undo/redo functionality, and style presets.",
          "**Custom Card Management (`CustomCardManager.js`, `EnhancedTCGController.js`):**",
          "IMPROVED: Robust system for creating, updating, deleting, and organizing custom cards.",
          "ADDED: Support for assigning custom cards to user-defined sets via `CustomSetTabs.js` and `CustomCardImportExport.js`.",
          "**Deck Persistence & Import (`DeckViewPanel.js`, `StorageManager.js`, `ImportValidator.js`):**",
          "IMPROVED: Work-in-progress decks are now automatically saved to local storage and reloaded on app start.",
          "ADDED: Unified `StorageManager.js` for consistent data handling, including chunking for large datasets.",
          "ADDED: `ImportValidator.js` for validating decklist formats before import.",
          "**Drag and Drop (`DeckViewPanel.js`, `CardContainer.js`):**",
          "IMPROVED: Drag and drop functionality for adding cards to the deck from search results.",
        ],
      },
      {
        title: "Performance & Optimization",
        items: [
          "**Optimized Image Loading (`ImageOptimizer.js`, `PkmnCard.js`):**",
          "ADDED: Introduced `ImageOptimizer.js` to manage image URLs (small/large versions).",
          "IMPROVED: Card displays now prioritize smaller resolution images for faster grid loading.",
          "IMPROVED: `PkmnCard.js` features improved image loading with caching (`ImageCacheService`), error handling, retries, and fallback displays.",
          "**Optimized Initial Load (`EnhancedCardSearchPanel.js`):**",
          "IMPROVED: Loads only the latest set by default, significantly reducing initial data fetch and rendering time.",
          "**Efficient Data Storage (`StorageManager.js`, `EnhancedTCGController.js`):**",
          "ADDED: Implemented chunking for custom card storage in `EnhancedTCGController.js` and generalized in `StorageManager.js` to handle large amounts of data.",
          "IMPROVED: Optimized storage for favorites by compressing card data.",
        ],
      },
      {
        title: "Bug Fixes",
        items: [
          "**Card Preview (`EnhancedCardSearchPanel.js`, `CardPreviewModal.js`):**",
          "FIXED: Resolved issues with the card preview button; clicking card images now reliably opens the preview modal.",
          "**Search Functionality (`EnhancedCardSearchPanel.js`):**",
          "FIXED: Corrected syntax errors in API query construction within `performSearch`, ensuring proper search execution and filter application.",
          "FIXED: Addressed issues with saving and loading work-in-progress decks for improved data integrity.",
          "**General UI & Logic:**",
          "FIXED: Numerous small fixes across components to support the above features and enhance stability.",
        ],
      },
      {
        title: "Code & System Refinements",
        items: [
          "**Modular Design:**",
          "ADDED: New utility modules (`ImageOptimizer.js`, `ImportValidator.js`, `StorageManager.js`).",
          "**Controller Logic (`EnhancedTCGController.js`, `TCGController.js`):**",
          "IMPROVED: Refined API interaction logic, including methods for fetching sets, rarities, and subtypes.",
          "IMPROVED: `query` method in `EnhancedTCGController.js` to handle API and custom card results, and correctly format API parameters.",
          "**Component Updates (`PkmnCard.js`, `CardContainer.js`, `CustomSetTabs.js`, etc.):**",
          "IMPROVED: Updated various components for better state management, prop handling, and UI consistency.",
          "**CSS Overhaul (`CardSearchPanel.module.css`):**",
          "IMPROVED: Comprehensive update to styles for improved layout, loading indicators, dark theme support, and overall visual polish.",
        ],
      },
    ],
  },
  {
    version: "1.2.0",
    date: "2025-05-17",
    title: "Custom Card & Export Enhancements",
    isOpen: false, 
    changes: [
      {
        title: "Major Changes",
        items: [
          "**Custom Card Creation System:**",
          "CHANGED: Replaced image upload with direct URL input for custom cards, simplifying the process and improving reliability.",
          "FIXED: Resolved storage quota errors previously encountered when creating multiple custom cards by storing only image URLs.",
          "ADDED: Image preview for direct URLs during custom card creation, with error validation to ensure link viability.",
          "IMPROVED: Enhanced compatibility with deck importers like PTCG Live and TCG Simulator by using standardized direct image URLs.",
          "**Deck Import/Export System:**",
          "FIXED: CSV exports now consistently use direct image URLs instead of `data:image` strings, ensuring better compatibility and smaller file sizes.",
          "IMPROVED: Exported CSV files are now significantly smaller due to the removal of embedded image data.",
          "ADDED: Default placeholder images are now used for different card types (Pokémon, Trainer, Energy) if a custom card's URL is invalid or missing during export.",
          "FIXED: Importing and exporting Trainer and Energy cards now works more reliably with accurate type and image handling.",
          "**Data Storage Optimizations:**",
          "REDUCED: Storage requirements for custom cards have been reduced by over 90% by storing image URLs instead of base64 encoded images.",
          "ADDED: Automatic cleanup of the oldest custom cards when the maximum storage limit (currently 50 cards) is reached, preventing browser storage quota errors.",
          "FIXED: Eliminated browser storage quota errors that could occur when saving a large number of custom cards.",
        ],
      },
      {
        title: "Technical Improvements",
        items: [
          "**Enhanced TCG Controller (`src/utils/TCGapi/EnhancedTCGController.js`):**",
          "CHANGED: Version upgraded to 1.4.0.",
          "REMOVED: Complex client-side image processing and compression for custom cards.",
          "ADDED: Direct URL storage mechanism for custom card images.",
          "IMPROVED: Error handling for storage limits and automatic cleanup of old custom card data.",
          "**Custom Card Creator (`src/components/modals/CustomCardCreator.js`):**",
          "REPLACED: File upload input with a direct URL input field for card images.",
          "ADDED: URL validation with visual feedback (e.g., success/error indicators) for the image link.",
          "ADDED: Live image preview directly from the provided URL within the creator modal.",
          "IMPROVED: Form validation and error handling for a smoother user experience.",
          "ADDED: Help text guiding users on compatible image hosts and URL types.",
          "**TCG Sim Controller (`src/utils/TCGsim/TCGSimController.js`):**",
          "ADDED: Enhanced export-friendly URL formatting. It now intelligently handles various URL types, including attempts to make Google Drive links viewable for export (though direct links are still preferred).",
          "ADDED: Robust placeholder images for different card types (Pokémon, Trainer, Energy) during export if an image URL is problematic.",
          "IMPROVED: CSV formatting (quoting fields) for better compatibility with a wider range of spreadsheet programs and simulators.",
          "IMPROVED: Error handling for malformed input during CSV import and export processes.",
        ],
      },
      {
        title: "Compatibility Notes",
        items: [
          "Direct image URLs (e.g., ending in `.png`, `.jpg`) must be used for custom cards for full compatibility, especially with external tools. These can be hosted on services like Imgur, ImgBB, or your own web server.",
          "While the application attempts to process Google Drive viewing page links for in-app display and export, they are not guaranteed to work reliably with all external simulators. Direct image links are strongly recommended.",
          "Existing custom cards created with `data:` URLs (from previous versions) will still display correctly within the application but will be exported using placeholder images to ensure compatibility and small file sizes.",
        ],
      },
      {
        title: "Known Issues",
        items: [
          "Google Drive or similar viewing page links (e.g., `drive.google.com/file/d/.../view`) might not render correctly in all external tools or simulators even after transformation. Always prefer direct image URLs.",
          "Only permanent, publicly accessible direct image URLs ending with common image file extensions (e.g., `.jpg`, `.png`, `.gif`) are guaranteed to be fully compatible for custom cards, especially for third-party tools.",
          "Image URLs must be publicly accessible and not require any login or special permissions to view.",
        ],
      },
      {
        title: "Summary",
        items: [
          "This update significantly improves the reliability and usability of custom cards, enhances compatibility with external deck importers/simulators, and resolves critical storage limitations, leading to a much smoother user experience.",
        ]
      }
    ],
  },
  {
    version: "1.1.0",
    date: "2025-04-15",
    title: "Proxy Support & Early Fixes",
    isOpen: false,
    changes: [
      {
        title: "What's New",
        items: ["ADDED: The ability to toggle between JP and English card proxies now available. Note, some cards will not have english proxies. Proxies are taken from JustInBasil."],
      },
      {
        title: "Bug Fixes",
        items: ["FIXED: Issue where cards imported with type \"Pokemon\" (instead of \"Pokémon\") failed to import."],
      },
      {
        title: "Supported Prereleased Sets",
        items: ["Twilight Masquerade", "Temporal Forces (logo only shown as example)"],
      }
    ]
  }
];

export const latestVersion = changelogData[0]; 

export default changelogData;
